import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { CircleDollarSign, Shield } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";

export default function Register() {
  const { user, register, setUser } = useAuth();
  const nav = useNavigate();
  const [search] = useSearchParams();
  const token = search.get("token");
  const [invite, setInvite] = useState(null);
  const [inviteError, setInviteError] = useState("");
  const [config, setConfig] = useState({ public_registration: true });
  const [form, setForm] = useState({ name: "", company_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/config").then((r) => setConfig(r.data)).catch(() => {});
    if (token) {
      api
        .get(`/invites/verify/${token}`)
        .then((r) => {
          setInvite(r.data);
          if (r.data.email) setForm((f) => ({ ...f, email: r.data.email }));
        })
        .catch((e) => setInviteError(formatApiErrorDetail(e?.response?.data?.detail) || "Davet doğrulanamadı"));
    }
  }, [token]);

  if (user) return <Navigate to="/" replace />;

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (token) {
        const r = await api.post("/auth/register-invite", {
          token,
          name: form.name,
          email: form.email,
          password: form.password,
        });
        setUser(r.data.user);
        nav("/", { replace: true });
      } else {
        await register(form);
        nav("/", { replace: true });
      }
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Kayıt başarısız");
    } finally {
      setBusy(false);
    }
  };

  // If public registration is closed and no invite token, show a locked screen
  if (!config.public_registration && !token) {
    return (
      <div className="auth-shell" data-testid="register-page-closed">
        <aside className="auth-hero">
          <div className="brand auth-brand">
            <div className="brand-mark"><CircleDollarSign size={20} /></div>
            <span>verafin</span>
          </div>
          <h1>Kapalı çalışma alanı.</h1>
          <p>Bu VeraFin örneğinde yeni kayıt kapalıdır. Yönetici tarafından size gönderilen davet linki ile katılabilirsiniz.</p>
        </aside>
        <section className="auth-card-wrap">
          <div className="auth-card">
            <Shield size={28} color="#6ca5ff" />
            <h2 style={{ marginTop: 14 }}>Davet gerekli</h2>
            <p className="auth-sub">Bu sunucuda kayıt olabilmek için geçerli bir davet linkiniz olmalı.</p>
            <div className="auth-foot">
              Hesabınız var mı? <Link to="/login" data-testid="go-login-link">Giriş yapın</Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="auth-shell" data-testid="register-page">
      <aside className="auth-hero">
        <div className="brand auth-brand">
          <div className="brand-mark"><CircleDollarSign size={20} /></div>
          <span>verafin</span>
        </div>
        <h1>{invite ? `${invite.company_name} sizi bekliyor.` : "Şirketinizi\nbirkaç dakikada kurun."}</h1>
        <p>{invite ? `Rolünüz: ${invite.role}. Hesabınızı oluşturun, hemen çalışma alanına katılın.` : "Kayıt tamamlanır tamamlanmaz Genel Bakış ekranı sizi bekliyor. Hesaplarınızı, carilerinizi ve işlemlerinizi hemen eklemeye başlayın."}</p>
      </aside>

      <section className="auth-card-wrap">
        <form className="auth-card" onSubmit={submit}>
          <div className="eyebrow">{invite ? "DAVET" : "HESAP OLUŞTUR"}</div>
          <h2>{invite ? "Ekibe katıl" : "Ücretsiz başlayın"}</h2>
          <p className="auth-sub">{invite ? `${invite.company_name} çalışma alanına ${invite.role} rolüyle katılıyorsunuz.` : "Kart bilgisi gerekmez. İstediğiniz an iptal edin."}</p>

          {inviteError ? <div className="form-error" data-testid="invite-error">{inviteError}</div> : null}

          <div className="form-grid">
            <label>
              Ad soyad
              <input
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                required
                data-testid="register-name-input"
              />
            </label>
            {invite ? null : (
              <label>
                Şirket adı
                <input
                  value={form.company_name}
                  onChange={(e) => set("company_name", e.target.value)}
                  required
                  data-testid="register-company-input"
                />
              </label>
            )}
          </div>

          <label>
            E-posta
            <input
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              required
              disabled={!!invite?.email}
              data-testid="register-email-input"
            />
          </label>
          <label>
            Parola (en az 6 karakter)
            <input
              type="password"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              required
              minLength={6}
              data-testid="register-password-input"
            />
          </label>

          {error ? <div className="form-error" data-testid="register-error">{error}</div> : null}

          <button className="primary-btn full" disabled={busy || !!inviteError} data-testid="register-submit-button">
            {busy ? "Oluşturuluyor…" : invite ? "Ekibe katıl" : "Hesabı oluştur"}
          </button>

          <div className="auth-foot">
            Zaten hesabınız var mı? <Link to="/login" data-testid="go-login-link">Giriş yapın</Link>
          </div>
        </form>
      </section>
    </div>
  );
}
